import {useState, useEffect} from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {CardWidget} from 'shells/card/card-widget/card-widget';
import {colors} from 'shells/colors/colors';
import {FormField} from 'shells/form/form-field';
import {CalendarRegularPenIcon} from 'shells/icon/svg/calendar-regular-pen-icon';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';
import {CharacterLimitedTextfield} from 'shells/textfield/character-limited-textfield';
import {TextFieldDebounced} from 'shells/textfield/textfield-debounced';
import {Body} from 'shells/typography/body';

import type {
    ChatActionsFragment_booking as BookingActionsFragment,
    ChatSettingsActionsInput,
} from 'nutshell-graphql-types';

import {AUTO_RESOLVE_ID} from '../../../../core/form/fields/consts';

import {useGetSchedulerMeetingsChat} from '../../graphql/hooks/use-get-scheduler-meetings-chat';

import {SAVE_STATE_KEYS, SAVE_DELAY, BOOK_WIDGET, MAX_LABEL_CHARACTER_LIMIT} from './consts';
import type {SaveStateKey, WidgetType} from './consts';
import {getSchedulerMeetingOptions, getStatusBadge} from './helpers';
import {LeadCreationToggleSection} from './lead-creation-toggle-section';
import {SelectTwoOptionEngagementEntity} from './select-two-line-engagement-entity';
import {useDebouncedSaveField} from './use-debounced-save-field';

interface Props {
    actionSettings: BookingActionsFragment;
    openWidget: WidgetType | null | undefined;
    setOpenWidget: (value?: WidgetType | null | undefined) => void;
    toastState: SavedSettingToastState;
    savingToastKey: SaveStateKey | null | undefined;
    handleSave: (updates: ChatSettingsActionsInput, saveStateKey: SaveStateKey) => Promise<any>;
    cardRef?: (arg1?: HTMLDivElement | null | undefined) => void;
}

export function BookingCard(props: Props) {
    const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

    const savedLabel = props.actionSettings.label || 'Book';
    const savedPipeline = props.actionSettings.shouldCreateLeadsPipelineId || AUTO_RESOLVE_ID;

    const [formConfirmationMessage, setFormConfirmationMessage] = useState<string>(
        props.actionSettings.formConfirmationMessage
    );

    const [isCreateLeadEnabled, setIsCreateLeadEnabled] = useState<boolean>(
        props.actionSettings.shouldCreateLeads
    );
    const [pipeline, setPipeline] = useState<string>(savedPipeline);
    const [schedulerMeetingId, setSchedulerMeetingId] = useState<string>(
        props.actionSettings.meetingId || ''
    );
    const [selectedMeeting, setSelectedMeeting] = useState<any>(null);

    const label = useDebouncedSaveField({
        savedValue: savedLabel,
        handleSave: (newLabel) =>
            props.handleSave({booking: {label: newLabel}}, SAVE_STATE_KEYS.BOOKING_LABEL),
        validation: {isRequired: true, maxLength: MAX_LABEL_CHARACTER_LIMIT},
    });

    const {schedulerMeetings, isLoading} = useGetSchedulerMeetingsChat();
    const meetingOptions = getSchedulerMeetingOptions(schedulerMeetings || []);
    const hasMeetingOptions = meetingOptions.length !== 0;

    useEffect(() => {
        if (schedulerMeetingId && schedulerMeetings) {
            const foundMeeting = schedulerMeetings.find(
                (meeting) => meeting.id === schedulerMeetingId
            );
            setSelectedMeeting(foundMeeting);
        }
    }, [schedulerMeetingId, schedulerMeetings, selectedMeeting]);

    useEffect(() => {
        // If openWidget's value changes, collapse the widget
        if (props.openWidget !== BOOK_WIDGET) {
            setIsCollapsed(true);
        }
    }, [props.openWidget]);

    useEffect(() => {
        setPipeline(savedPipeline);
    }, [savedPipeline]);

    return (
        <CardWidget
            cardRef={props.cardRef}
            title='Booking'
            hasBorder={true}
            collapsibleProps={{
                isCollapsed,
                onToggle: () => {
                    setIsCollapsed(!isCollapsed);
                    props.setOpenWidget(isCollapsed ? BOOK_WIDGET : null);
                },
            }}
            type='BOOK'
            secondaryTitle={getStatusBadge(props.actionSettings.isConfigured)}
            rowsHavePadding={false}
        >
            {isLoading ? (
                <LoadingPage padding={16} />
            ) : (
                <div className='my-16 mx-12'>
                    {hasMeetingOptions ? (
                        <>
                            <FormField
                                innerId='action-label'
                                label='Action label'
                                saveToastState={
                                    props.savingToastKey === SAVE_STATE_KEYS.BOOKING_LABEL
                                        ? props.toastState
                                        : 'initial'
                                }
                            >
                                <CharacterLimitedTextfield
                                    value={label.value}
                                    onChange={label.onChange}
                                    onBlur={label.onBlur}
                                    maxLength={MAX_LABEL_CHARACTER_LIMIT}
                                    placeholder='Book'
                                    isDebouncedSave={true}
                                    debouncedSaveDelay={SAVE_DELAY}
                                />
                            </FormField>
                            <FormField
                                innerId='scheduler-link'
                                label='Scheduler link'
                                saveToastState={
                                    props.savingToastKey === SAVE_STATE_KEYS.BOOKING_MEETING_ID
                                        ? props.toastState
                                        : 'initial'
                                }
                            >
                                <SelectTwoOptionEngagementEntity
                                    options={meetingOptions}
                                    placeholder='Select a meeting…'
                                    value={meetingOptions.find(
                                        (option) => option.value === schedulerMeetingId
                                    )}
                                    onChange={(option) => {
                                        setSchedulerMeetingId(option.value);
                                        props.handleSave(
                                            {booking: {meetingId: option.value}},
                                            SAVE_STATE_KEYS.BOOKING_MEETING_ID
                                        );
                                    }}
                                    isLoading={false}
                                    shouldShowEditLink={Boolean(selectedMeeting)}
                                    editLabel='Edit meeting'
                                    editLink={selectedMeeting && selectedMeeting.htmlUrlPath}
                                    autoFocus={!schedulerMeetingId}
                                    openMenuOnFocus={true}
                                    isSearchable={true}
                                    customNoOptionsMessage={{
                                        // We render a different component when no meetings are found
                                        // This is just to satisfy prop type
                                        text: 'No meetings available yet. Create one via Scheduler.',
                                    }}
                                />
                            </FormField>
                            <FormField
                                innerId='confirmation-message'
                                label='Confirmation message after booking meeting'
                                saveToastState={
                                    props.savingToastKey ===
                                    SAVE_STATE_KEYS.BOOKING_CONFIRMATION_MSG
                                        ? props.toastState
                                        : 'initial'
                                }
                            >
                                <TextFieldDebounced
                                    value={formConfirmationMessage}
                                    onChange={(newMessage) => {
                                        setFormConfirmationMessage(newMessage);
                                        props.handleSave(
                                            {
                                                booking: {
                                                    formConfirmationMessage: newMessage,
                                                },
                                            },
                                            SAVE_STATE_KEYS.BOOKING_CONFIRMATION_MSG
                                        );
                                    }}
                                    multiline={true}
                                    placeholder='Thank you for contacting us, looking forward to talking more about your needs!'
                                    delay={SAVE_DELAY}
                                />
                            </FormField>
                            <LeadCreationToggleSection
                                labelContent='Create a lead for each booking'
                                description='A lead will be created in this pipeline after a visitor books a meeting via the chat widget'
                                isOn={isCreateLeadEnabled}
                                onToggle={() => {
                                    setIsCreateLeadEnabled(!isCreateLeadEnabled);
                                    props.handleSave(
                                        {
                                            booking: {
                                                shouldCreateLeads: !isCreateLeadEnabled,
                                            },
                                        },
                                        SAVE_STATE_KEYS.BOOKING_SHOULD_CREATE_LEADS
                                    );
                                }}
                                toastState={props.toastState}
                                savingToastKey={props.savingToastKey}
                                toggleSaveKey={SAVE_STATE_KEYS.BOOKING_SHOULD_CREATE_LEADS}
                                pipelineSaveKey={SAVE_STATE_KEYS.BOOKING_LEADS_PIPELINE_ID}
                                pipeline={pipeline}
                                onSelectPipeline={(newPipeline) => {
                                    setPipeline(newPipeline);
                                    props.handleSave(
                                        {
                                            booking: {
                                                shouldCreateLeadsPipelineId:
                                                    newPipeline === AUTO_RESOLVE_ID
                                                        ? null
                                                        : newPipeline,
                                            },
                                        },
                                        SAVE_STATE_KEYS.BOOKING_LEADS_PIPELINE_ID
                                    );
                                }}
                                showPipeline={isCreateLeadEnabled}
                            />
                        </>
                    ) : (
                        <Stack spacing={8} alignItems='center'>
                            {/* @ts-expect-error */}
                            <CalendarRegularPenIcon size={24} fill={colors.greyLt} />
                            <Body>No Scheduler meetings available</Body>
                            <Link
                                // @ts-expect-error
                                as={RoutedLink}
                                newTab={true}
                                variant='button-primary'
                                to='/sales/scheduler'
                                size='big'
                            >
                                Set up Scheduler
                            </Link>
                        </Stack>
                    )}
                </div>
            )}
        </CardWidget>
    );
}
